import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Archive, X, Save, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { listPackages, createPackage, updatePackage, archivePackage } from '../../services/adminApi';

const LABEL_PRESETS = [
  '', 'Popular', 'Best Value', 'Best value for casual users',
  'Best value pack', 'Most Popular', 'Limited Time'
];

const emptyPkg = {
  name: '', amount: 0, credits: 0, bonus_credits: 0,
  description: '', label: '', active: true, type: 'credits', sort_order: 100
};

const CreditPackagesTab = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // package_id or 'new'
  const [form, setForm] = useState(emptyPkg);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => { load(); }, [showArchived]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listPackages(showArchived);
      setPackages(res.data);
    } catch (err) {
      setError('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (pkg) => {
    setEditing(pkg.package_id);
    setForm({ ...pkg });
    setError('');
  };

  const startNew = () => {
    setEditing('new');
    setForm({ ...emptyPkg });
    setError('');
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptyPkg);
    setError('');
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount) || 0,
        credits: parseInt(form.credits) || 0,
        bonus_credits: parseInt(form.bonus_credits) || 0,
        sort_order: parseInt(form.sort_order) || 100
      };
      if (editing === 'new') {
        await createPackage(payload);
      } else {
        await updatePackage(editing, payload);
      }
      await load();
      cancel();
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const archive = async (packageId) => {
    if (!window.confirm('Archive this package? Users will no longer see it.')) return;
    try {
      await archivePackage(packageId);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to archive');
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#009E60] border-t-transparent mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">
            Existing packages preserved. Prices remain unchanged unless you manually edit them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="show-archived" />
            Show archived
          </label>
          <Button
            onClick={startNew}
            className="bg-[#009E60] hover:bg-[#007A4B] rounded-full"
            data-testid="new-package-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> New Package
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm">{error}</div>
      )}

      {editing === 'new' && (
        <PackageForm form={form} setForm={setForm} save={save} cancel={cancel} saving={saving} isNew />
      )}

      <div className="space-y-3">
        {packages.map(pkg => (
          <div
            key={pkg.package_id}
            className={`bg-white border rounded-2xl overflow-hidden ${pkg.archived ? 'opacity-60' : ''}`}
            data-testid={`package-row-${pkg.package_id}`}
          >
            {editing === pkg.package_id ? (
              <div className="p-4">
                <PackageForm form={form} setForm={setForm} save={save} cancel={cancel} saving={saving} />
              </div>
            ) : (
              <div className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{pkg.name}</span>
                    {pkg.label && (
                      <span className="text-xs bg-[#FFCE00]/20 text-gray-800 px-2 py-0.5 rounded-full">{pkg.label}</span>
                    )}
                    {!pkg.active && (
                      <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full">Inactive</span>
                    )}
                    {pkg.archived && (
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Archived</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {pkg.type === 'subscription'
                      ? `Subscription · $${pkg.amount.toFixed(2)}`
                      : `${pkg.credits} credits${pkg.bonus_credits ? ` (+ ${pkg.bonus_credits} bonus)` : ''} · $${pkg.amount.toFixed(2)}`}
                  </div>
                  {pkg.description && (
                    <div className="text-xs text-gray-400 mt-1 truncate">{pkg.description}</div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => startEdit(pkg)}
                    className="p-2 hover:bg-gray-100 rounded-full text-gray-600"
                    data-testid={`edit-${pkg.package_id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  {!pkg.archived && (
                    <button
                      onClick={() => archive(pkg.package_id)}
                      className="p-2 hover:bg-red-50 rounded-full text-red-500"
                      data-testid={`archive-${pkg.package_id}`}
                    >
                      <Archive className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const PackageForm = ({ form, setForm, save, cancel, saving, isNew }) => (
  <div className="space-y-3 bg-[#009E60]/5 border-2 border-[#009E60]/20 rounded-2xl p-4">
    <h3 className="font-semibold text-gray-900">{isNew ? 'New Package' : 'Edit Package'}</h3>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-sm">Name</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" data-testid="pkg-name" />
      </div>
      <div>
        <Label className="text-sm">Type</Label>
        <select
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
          className="mt-1 w-full h-10 rounded-xl border border-gray-200 px-3 bg-white"
          data-testid="pkg-type"
        >
          <option value="credits">Credits</option>
          <option value="subscription">Subscription</option>
        </select>
      </div>
      <div>
        <Label className="text-sm">Price ($)</Label>
        <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 rounded-xl" data-testid="pkg-amount" />
      </div>
      <div>
        <Label className="text-sm">Credits</Label>
        <Input type="number" value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} className="mt-1 rounded-xl" data-testid="pkg-credits" />
      </div>
      <div>
        <Label className="text-sm">Bonus Credits</Label>
        <Input type="number" value={form.bonus_credits} onChange={(e) => setForm({ ...form, bonus_credits: e.target.value })} className="mt-1 rounded-xl" data-testid="pkg-bonus" />
      </div>
      <div>
        <Label className="text-sm">Sort Order</Label>
        <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1 rounded-xl" />
      </div>
    </div>
    <div>
      <Label className="text-sm">Description</Label>
      <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1 rounded-xl resize-none" data-testid="pkg-desc" />
    </div>
    <div>
      <Label className="text-sm">Label / Badge</Label>
      <select
        value={form.label}
        onChange={(e) => setForm({ ...form, label: e.target.value })}
        className="mt-1 w-full h-10 rounded-xl border border-gray-200 px-3 bg-white"
        data-testid="pkg-label"
      >
        {LABEL_PRESETS.map(l => <option key={l} value={l}>{l || 'No label'}</option>)}
      </select>
    </div>
    <div className="flex items-center justify-between">
      <label className="flex items-center gap-2 text-sm">
        <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="pkg-active" />
        Active (visible to users)
      </label>
    </div>
    <div className="flex gap-2 pt-2">
      <Button onClick={cancel} variant="outline" className="flex-1 rounded-xl" disabled={saving}>
        <X className="w-4 h-4 mr-1" /> Cancel
      </Button>
      <Button onClick={save} disabled={saving} className="flex-1 bg-[#009E60] hover:bg-[#007A4B] rounded-xl" data-testid="pkg-save">
        <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
      </Button>
    </div>
  </div>
);

export default CreditPackagesTab;
